/**
 * @file Wrapper of header with search component for the OrderOverview page
 * @author Max van Loosbroek
 */

import {
  Checkbox,
  createStyles,
  FormControlLabel,
  FormGroup,
  makeStyles,
  Radio
} from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { hideColumn, showColumn } from '../redux/reducer'
import { ORDER_OVERVIEW_FILTERS } from '../constants'
import { SearchType } from 'tinybots-react-components/lib/components/DashboardComponents/Search'
import { TbDashHeader, TbDashSearch } from 'tinybots-react-components'
import { usePrevious } from 'tinybots-react-components/lib/hooks'
import { groupParamsByKey } from '../../../../common/utilities/groupParamsByKey'
import {
  OrderOverviewColumn,
  OrderOverviewFilter,
  OrderOverviewQueryParams
} from '../../types/types'

export const orderOverviewSearchItems: SearchType[] = [
  {
    searchLabel: 'relation & serial',
    searchType: 'robotRelation',
    placeholder: 'Search for relation or serial'
  },
  {
    searchLabel: 'box',
    searchType: 'boxNumber',
    placeholder: 'Search for box number'
  },
  {
    searchLabel: 'Team or client id',
    searchType: 'teamClient',
    placeholder: 'Search by team or client'
  }
]

const useStyles = makeStyles(() =>
  createStyles({
    toggles: {
      height: '100%'
    },
    formRow: {
      flexWrap: 'nowrap',
      height: '50%',
      alignItems: 'center'
    },
    formRowTitle: {
      fontFamily: 'Montserrat-Bold',
      color: 'white',
      fontSize: 12,
      width: 70,
      paddingTop: 13
    },
    label: {
      fontSize: 11,
      color: 'white'
    },
    control: {
      width: 55,
      marginLeft: 5
    },
    checkbox: {
      padding: 0,
      color: 'white',
      '&$checked': {
        color: 'white'
      }
    }
  })
)

export interface OrderOverviewHeaderProps {
  params: OrderOverviewQueryParams
  columns: OrderOverviewColumn[]
  setUrlParams: (params: OrderOverviewQueryParams) => void
  onSubmit: (props: { searchTerm: string, searchType: string }) => void
  color: string
}

export const handleFilter = (
  { filterType, filterTerm }: OrderOverviewFilter,
  setUrlParams: (params: OrderOverviewQueryParams) => void,
  browserWindow: any = window
) => {
  const currentUrlParams = new URLSearchParams(window.location.search)
  if (!filterTerm || filterTerm === 'all') {
    currentUrlParams.delete(filterType)
  } else {
    currentUrlParams.set(filterType, filterTerm)
  }
  setUrlParams(groupParamsByKey(currentUrlParams as any))
  browserWindow.history.replaceState(
    null,
    null,
    location.origin + location.pathname + '?' + currentUrlParams.toString()
  )
}

export const OrderOverviewHeader: React.FunctionComponent<OrderOverviewHeaderProps> = ({
  onSubmit,
  params,
  setUrlParams,
  columns,
  color
}) => {
  const previousParams = usePrevious(params)
  const [searchType, setSearchType] = useState(params.searchType)
  const [searchTerm, setSearchTerm] = useState(params.searchTerm)
  const classes = useStyles()
  const dispatch = useDispatch()
  const handleToggleColumn = (c: OrderOverviewColumn) => {
    if (c.hidden) {
      dispatch(showColumn(c.key))
    } else {
      dispatch(hideColumn(c.key))
    }
  }

  useEffect(() => {
    if (previousParams?.searchTerm !== params?.searchTerm) {
      setSearchTerm(params.searchTerm)
    }
    if (previousParams?.searchType !== params?.searchType) {
      setSearchType(params.searchType)
    }
  }, [previousParams, params])

  return (
    <TbDashHeader color={color}>
      <TbDashSearch
        color={color}
        searchTerm={searchTerm}
        searchType={searchType}
        items={orderOverviewSearchItems}
        onSubmit={onSubmit}
        setActiveSearchType={type => setSearchType(type.searchType)}
        setSearchTerm={setSearchTerm}
      ></TbDashSearch>
      <div className={classes.toggles} aria-label='OrderOverview Settings'>
        <FormGroup row className={classes.formRow}>
          <span className={classes.formRowTitle}>Filters: </span>
          {ORDER_OVERVIEW_FILTERS.map((f, i) => {
            return (
              <FormControlLabel
                key={`filter-${i}`}
                label={f.label}
                labelPlacement='top'
                classes={{ label: classes.label, root: classes.control }}
                control={
                  <Radio
                    className={classes.checkbox}
                    checked={
                      params[f.filterType] === f.filterTerm ||
                      (!params[f.filterType] && f.filterTerm === 'all')
                    }
                    onChange={() => handleFilter(f, setUrlParams)}
                    value='a'
                    color='default'
                    name='radio-buttons'
                    inputProps={{ 'aria-label': 'A' }}
                  />
                }
              />
            )
          })}
        </FormGroup>
        <FormGroup row className={classes.formRow}>
          <span className={classes.formRowTitle}>Columns: </span>
          {columns.map((c, i) => {
            return (
              <FormControlLabel
                key={`column-${c.key}`}
                label={c.headerName}
                labelPlacement='top'
                classes={{ label: classes.label, root: classes.control }}
                control={
                  <Checkbox
                    checked={!c.hidden}
                    onChange={() => handleToggleColumn(c)}
                    className={classes.checkbox}
                    color='default'
                  />
                }
              />
            )
          })}
        </FormGroup>
      </div>
    </TbDashHeader>
  )
}
